import { createSelector } from "@reduxjs/toolkit";
import { applyPersonalization } from "../../Utility/personalizationEngine";

// Stable fallback references — returning a fresh [] on every call (state?.x || [])
// defeats createSelector's reference-equality memoization until the real data
// loads, forcing a recompute on every dispatch during the loading race instead
// of just when the underlying slice actually changes.
const EMPTY_ARRAY = [];

const selectRawSectionMasters = (state) => state?.homePageSliceReducer?.homePageData?.sectionMasters || EMPTY_ARRAY;
const selectRawSectionContentMasters = (state) =>
  state?.homePageSliceReducer?.homePageData?.sectionContentMasters || EMPTY_ARRAY;
const selectGenericRules = (state) => state?.personalizationSliceReducer?.generic?.rules || EMPTY_ARRAY;
const selectMemberRules = (state) => state?.personalizationSliceReducer?.member?.rules || EMPTY_ARRAY;
const selectCurrentMemberIdentifier = (state) => state?.validateLoginTokenSliceReducer?.data?.memberIdentifier || null;

// Memoized by reference equality of its inputs — recomputes only when the
// raw Home Page data or the rule set actually changes, not on every render
// of every section. Generic rules apply to everyone, unfiltered. Member
// rules are already scoped to the current member by which file got fetched
// (one file per member, named by memberIdentifier — see personalizationApi.js),
// but the memberIdentifier check here stays as a harmless safety net in case
// a member's file ever carries a rule meant for someone else. Generic rules
// go first as the baseline, member rules layer on top, matching the merge
// order the engine expects.
export const selectPersonalizedHomeData = createSelector(
  [selectRawSectionMasters, selectRawSectionContentMasters, selectGenericRules, selectMemberRules, selectCurrentMemberIdentifier],
  (sectionMasters, sectionContentMasters, genericRules, memberRules, currentMemberIdentifier) => {
    const applicableMemberRules = memberRules.filter(
      (rule) => !rule?.memberIdentifier || String(rule.memberIdentifier) === String(currentMemberIdentifier)
    );
    const rules = [...genericRules, ...applicableMemberRules];

    if (!rules.length) {
      return { sectionMasters, sectionContentMasters, sectionOverrides: {} };
    }
    return applyPersonalization({ sectionMasters, sectionContentMasters }, rules);
  }
);
